import {isWithinInterval} from 'date-fns'
import {ContentParams} from '../../../types/store/content'
import {DateUtils} from '../../../utils/date-utils'
import {DeliveryOption, DaysAvailable} from '../../../types/cms/delivery-banner'
import {SlotItem} from '../../../types/cms'

export const DELIVERY_BANNER_SCHEMA = 'https://www.iceland.co.uk/content/delivery-banner.json'

export interface RapidDeliveryAvailability {
  available: boolean
  resolved: boolean
  postcode?: string | null
}

const isDeliveryTypeAvailable = (deliveryOption: DeliveryOption, params?: ContentParams): boolean => {
  if (deliveryOption.deliveryType === 'None' || !deliveryOption.deliveryOptions) {
    return false
  }

  const currentDate =
    params?.isContentPreview && params?.previewTimestamp
      ? new Date(params.previewTimestamp)
      : new Date()
  const {daysAvailable, timesAvailableBetween} = deliveryOption.deliveryOptions
  const currentDay = DateUtils.format(currentDate, 'EEEE').toLowerCase() as keyof Omit<DaysAvailable, 'restrictDays'>
  const isAvailableToday = !daysAvailable?.restrictDays || daysAvailable[currentDay] === true

  if (!isAvailableToday) {
    return false
  }

  const {restrictTime, startTime, endTime} = timesAvailableBetween

  if (!restrictTime) {
    return true
  }

  if (!startTime || !endTime) {
    return false
  }

  const currentDateStr = DateUtils.format(currentDate, 'yyyy-MM-dd')

  return isWithinInterval(currentDate, {
    start: new Date(`${currentDateStr}T${startTime}`),
    end: new Date(`${currentDateStr}T${endTime}`),
  })
}

const hasRenderableBannerContent = (deliveryOption: DeliveryOption): boolean => {
  const {useImage, backgroundImage, bannerColor} = deliveryOption.deliveryOptions?.bannerBackground || {}

  return useImage
    ? Boolean(backgroundImage?.images?.length)
    : Boolean(bannerColor?.color?.trim())
}

export const getDeliveryBannerAvailability = (
  deliveryTypes: DeliveryOption[],
  params: ContentParams | undefined,
  rapidDelivery: RapidDeliveryAvailability,
) => {
  const hasRapidDeliveryOption = deliveryTypes.some((deliveryType) => deliveryType.deliveryType === 'Rapid')
  const isRapidAvailabilityLoading =
    !params?.isContentVisualization &&
    Boolean(rapidDelivery.postcode) &&
    hasRapidDeliveryOption &&
    !rapidDelivery.resolved

  const availableDeliveryTypes = deliveryTypes.filter((deliveryType) => {
    if (
      deliveryType.deliveryType === 'Rapid' &&
      !params?.isContentVisualization &&
      !isRapidAvailabilityLoading &&
      !rapidDelivery.available
    ) {
      return false
    }

    return isDeliveryTypeAvailable(deliveryType, params) && hasRenderableBannerContent(deliveryType)
  })

  return {availableDeliveryTypes, isRapidAvailabilityLoading}
}

export const hasAvailableDeliveryBannerContent = (
  items: SlotItem[],
  params: ContentParams | undefined,
  rapidDelivery: RapidDeliveryAvailability,
): boolean =>
  items.some((item) => {
    if (item?._meta?.schema !== DELIVERY_BANNER_SCHEMA) {
      return true
    }

    const deliveryTypes = [
      item.deliveryType1,
      item.deliveryType2,
      item.deliveryType3,
      item.deliveryType4,
      item.deliveryType5,
      item.deliveryType6,
    ].filter((deliveryType): deliveryType is DeliveryOption => !!deliveryType)

    return getDeliveryBannerAvailability(deliveryTypes, params, rapidDelivery).availableDeliveryTypes.length > 0
  })